import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box as MuiBox,
    Typography as MuiTypography,
    IconButton as MuiIconButton,
    Button as MuiButton,
    Chip as MuiChip,
    Paper as MuiPaper,
    TextField as MuiTextField,
    Tooltip as MuiTooltip,
    Menu as MuiMenu,
    MenuItem as MuiMenuItem,
    ListItemIcon as MuiListItemIcon,
    ListItemText as MuiListItemText
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Share as ShareIcon,
    Download as DownloadIcon,
    MoreVert as MoreVertIcon,
    Description as DescriptionIcon,
    Business as BusinessIcon,
    Code as CodeIcon,
    AccountTree as DiagramIcon,
    GridOn as WireframeIcon,
    Article as MarkdownIcon,
    Close as CloseIcon,
    KeyboardArrowDown as ChevronDownIcon,
    InsertDriveFileOutlined as ExplorerIcon,
    Search as SearchIcon,
    // Git icon — using AccountTree as a stand-in; swap for a custom SVG later
    AccountTreeOutlined as GitIcon
} from "@mui/icons-material";
import { WorkProduct, Feature, Initiative } from "../types";
// AiChatIcon not used in CODE views — AI Assistant is disabled for code editor
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import WorkProductEditorFileList from "./WorkProductEditor_FileList";
import fetchWithAuth from "@/api/secureFetch";
import { useAppSelector } from "@/state/hooks";
import { createGitFileContentApi, getGitFilesApi, getProjectRepositoryIdApi } from "@/api/api";
import { syncWorkProductCodeArtifact } from "@/api/workproduct/documents";
import { getFileIcon, FILE_ICON_SIZE } from "@/utils/fileIcons";
import { getHostDocumentBridge } from "@/components/aiBridge";
import { MonacoSuggestionController } from "./codeEditor/MonacoSuggestionController";
import { MarkdownPre } from "./codeEditor/MermaidDiagram";
import { ViewOnlyIndicator } from "@/security/access";
import { useCollabSession } from "@/collab/CollabSessionContext";
import CollabAvatarStack from "@/collab/CollabAvatarStack";
import { dedupePeersByUserAndTarget, usePresence } from "@/collab/usePresence";
import { presenceTarget, useActivityPublisher } from "@/collab/activityAwareness";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

// Default context data for when feature/project is not available (Product level)
const DEFAULT_CONTEXT_DATA = {
    feature: {
        id: null,
        name: "Product Level",
        description: "Product-level work product"
    },
    project: {
        projectid: null,
        project_name: "Project",
        project_description: "Project-level context"
    }
};

/** Represents a single open editor tab */
interface EditorTab {
    fileId: number;
    filePath: string;
    filename: string;
    content: string;
    language: string;
    dirty?: boolean;
}

interface WorkProductEditorProps {
    workProduct: WorkProduct | null;
    onBack: () => void;
    onJiraSyncOpen?: (context: "workproduct") => void;
    onAIChatToggle?: () => void;
    selectedProject?: Initiative | null;
    selectedFeature?: Feature | null;
    AIChatToggled?: boolean;
    readOnly?: boolean;
}

const WorkProductEditor_Code: React.FC<WorkProductEditorProps> = ({ workProduct, onBack, onAIChatToggle, AIChatToggled, selectedProject, selectedFeature, readOnly = true }) => {
    const authToken = useAppSelector(state => state.clientToken.token);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const [selectedSection, setSelectedSection] = useState<any>(null);

    const [repositoryId, setRepositoryId] = useState<number | null>(null);
    const [isLoadingRepo, setIsLoadingRepo] = useState(true);

    // ── Activity bar panel state ────────────────────────────────────────
    type SidePanel = "explorer" | "search" | "git";
    const [activeSidePanel, setActiveSidePanel] = useState<SidePanel>("explorer");

    // ── Tab state ───────────────────────────────────────────────────────
    const [openTabs, setOpenTabs] = useState<EditorTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<number | null>(null);
    const [activeView, setActiveView] = useState<"code" | "markdown">("code");
    const [editorFocused, setEditorFocused] = useState(false);
    const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
    const collabSession = useCollabSession();
    const presencePeers = dedupePeersByUserAndTarget(usePresence());
    const activeFileTarget = presenceTarget("file", activeTabId);
    useActivityPublisher(
        collabSession?.status === "connected" ? collabSession.provider : undefined,
        activeFileTarget,
        !readOnly && editorFocused ? "editing" : "viewing"
    );

    // ── Drag-and-drop tab reordering ────────────────────────────────────
    const [dragTabId, setDragTabId] = useState<number | null>(null);
    const [dropTargetId, setDropTargetId] = useState<number | null>(null);

    const handleTabDragStart = useCallback((e: React.DragEvent, fileId: number) => {
        setDragTabId(fileId);
        e.dataTransfer.effectAllowed = "move";
        // Make the drag ghost semi-transparent
        if (e.currentTarget instanceof HTMLElement) {
            e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
        }
    }, []);

    const handleTabDragOver = useCallback((e: React.DragEvent, fileId: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragTabId !== null && fileId !== dragTabId) {
            setDropTargetId(fileId);
        }
    }, [dragTabId]);

    const handleTabDrop = useCallback((e: React.DragEvent, targetFileId: number) => {
        e.preventDefault();
        if (dragTabId === null || dragTabId === targetFileId) return;

        setOpenTabs(prev => {
            const tabs = [...prev];
            const dragIdx = tabs.findIndex(t => t.fileId === dragTabId);
            const dropIdx = tabs.findIndex(t => t.fileId === targetFileId);
            if (dragIdx === -1 || dropIdx === -1) return prev;

            // Remove dragged tab and insert at drop position
            const [dragged] = tabs.splice(dragIdx, 1);
            tabs.splice(dropIdx, 0, dragged);
            return tabs;
        });

        setDragTabId(null);
        setDropTargetId(null);
    }, [dragTabId]);

    const handleTabDragEnd = useCallback(() => {
        setDragTabId(null);
        setDropTargetId(null);
    }, []);

    // ── Tab overflow detection ──────────────────────────────────────────
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabsOverflow, setTabsOverflow] = useState(false);
    const [tabMenuAnchor, setTabMenuAnchor] = useState<HTMLElement | null>(null);

    // Detect when the tab container is overflowing
    useEffect(() => {
        const el = tabContainerRef.current;
        if (!el) return;

        const checkOverflow = () => {
            setTabsOverflow(el.scrollWidth > el.clientWidth);
        };

        // Check immediately and on resize
        checkOverflow();
        const observer = new ResizeObserver(checkOverflow);
        observer.observe(el);

        return () => observer.disconnect();
    }, [openTabs]);

    // Resizable panel state
    const [leftPanelWidth, setLeftPanelWidth] = useState(480);
    const [isResizing, setIsResizing] = useState(false);
    const resizeStartX = React.useRef(0);
    const resizeStartWidth = React.useRef(480);
    const MIN_PANEL_WIDTH = 280;
    const MAX_PANEL_WIDTH = 800;

    // Derive active tab from state
    const activeTab = openTabs.find(t => t.fileId === activeTabId) ?? null;

    // Live mirror of openTabs for closures (content accessor / accept handler).
    const openTabsRef = useRef<EditorTab[]>(openTabs);
    const editorRef = useRef<any>(null);
    const activeTabIdRef = useRef<number | null>(activeTabId);
    const saveQueuesRef = useRef(new Map<number, Promise<boolean>>());
    useEffect(() => {
        openTabsRef.current = openTabs;
        activeTabIdRef.current = activeTabId;
    }, [openTabs, activeTabId]);

    const persistFileContent = useCallback(
        async (fileId: number, filename: string, content: string): Promise<boolean> => {
            if (!authToken) {
                console.error("Cannot save file: no authentication token available");
                return false;
            }
            // Serialize revisions for each file. Without this, an older
            // debounced request can finish after a newer accepted edit and
            // become the row returned by GET .../content after a refresh.
            const previous = saveQueuesRef.current.get(fileId) ?? Promise.resolve(true);
            const request = previous
                .catch(() => false)
                .then(async () => {
                    await createGitFileContentApi(fileId, { content }, authToken);
                    return true;
                });
            saveQueuesRef.current.set(fileId, request);
            try {
                await request;
                // Do not clear dirty if more typing happened while this request
                // was in flight; the newer buffer still needs its own save.
                setOpenTabs(prev =>
                    prev.map(tab =>
                        tab.fileId === fileId && tab.content === content
                            ? { ...tab, dirty: false }
                            : tab
                    )
                );
                return true;
            } catch (error) {
                console.error(`Error saving ${filename}:`, error);
                return false;
            } finally {
                if (saveQueuesRef.current.get(fileId) === request) {
                    saveQueuesRef.current.delete(fileId);
                }
            }
        },
        [authToken]
    );

    const controllerRef = useRef<MonacoSuggestionController | null>(null);

    // A code edit can be staged while a Markdown file is showing its rendered
    // preview (and Monaco is therefore unmounted). Reveal the edited file's
    // source view; mounting Monaco then replays the adapter's pending edit.
    useEffect(() => {
        const { monacoAdapter } = getHostDocumentBridge();
        monacoAdapter.setReviewRequestHandler((edit) => {
            setActiveTabId(edit.fileId);
            setActiveView("code");
        });
        return () => monacoAdapter.setReviewRequestHandler(null);
    }, []);

    const handleEditorDidMount = useCallback((editor: any, monaco: any) => {
        editorRef.current = editor;
        const { monacoAdapter } = getHostDocumentBridge();

        monacoAdapter.setContentAccessor((fileId) => {
            const ed = editorRef.current;
            if (ed && fileId === activeTabIdRef.current) {
                const model = ed.getModel();
                if (model) return model.getValue();
            }
            const tab = openTabsRef.current.find((t) => t.fileId === fileId);
            return tab ? tab.content : null;
        });
        if (readOnly) {
            monacoAdapter.setStagingHandler(null);
            return;
        }

        const controller = new MonacoSuggestionController(editor, monaco, {
            onAccept: (fileId, newContent) => {
                setOpenTabs((prev) =>
                    prev.map((t) => (t.fileId === fileId ? { ...t, content: newContent, dirty: true } : t))
                );
                const tab = openTabsRef.current.find(t => t.fileId === fileId);
                void persistFileContent(fileId, tab?.filename ?? `file ${fileId}`, newContent);
            },
            resolve: (editId, status, reason) => monacoAdapter.resolveEdit(editId, status, reason)
        });
        controllerRef.current = controller;

        monacoAdapter.setStagingHandler((edit) => controller.stage(edit));
        // Initialize the controller's active file immediately. The [openTabs,
        // activeTabId] sync effect only calls setActiveFile when those change;
        // if the file was already open+active before Monaco mounted (the common
        // case), that effect won't re-run, leaving activeFileId null and causing
        // stage() to store agent edits without rendering them. Seed it here.
        controller.setActiveFile(activeTabIdRef.current);
    }, [persistFileContent, readOnly]);

    const handleEditorWillUnmount = useCallback(() => {
        const { monacoAdapter } = getHostDocumentBridge();
        // Do not leave the adapter pointing at a controller whose Monaco editor
        // was disposed when Markdown preview replaced the source view.
        monacoAdapter.setStagingHandler(null);
        monacoAdapter.setContentAccessor(null);
        controllerRef.current?.dispose();
        controllerRef.current = null;
        editorRef.current = null;
    }, []);

    // This @monaco-editor/react version has no onUnmount callback. React runs
    // this effect after the conditional render removes Monaco, which is early
    // enough to detach the disposed controller before an agent can stage into
    // the preview-only view.
    useEffect(() => {
        if (activeView === "markdown") handleEditorWillUnmount();
    }, [activeView, handleEditorWillUnmount]);

    // Configure Monaco Editor before mount to disable validation
    const handleEditorWillMount = (monaco: any) => {
        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: true
        });
        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: true
        });
    };

    // Helper function to determine language from file path
    const getLanguageFromPath = (filePath: string): string => {
        const extension = filePath.split(".").pop()?.toLowerCase();
        const languageMap: { [key: string]: string } = {
            js: "javascript",
            jsx: "javascript",
            ts: "javascript",
            tsx: "javascript",
            py: "python",
            java: "java",
            c: "c",
            cpp: "cpp",
            cs: "csharp",
            css: "css",
            scss: "scss",
            html: "html",
            xml: "xml",
            json: "json",
            md: "markdown",
            sql: "sql",
            sh: "shell",
            yaml: "yaml",
            yml: "yaml",
            go: "go",
            rs: "rust",
            php: "php",
            rb: "ruby",
            swift: "swift",
            kt: "kotlin",
            r: "r",
            pl: "perl",
            lua: "lua"
        };
        return languageMap[extension || ""] || "plaintext";
    };

    // Helper function to get content type icon and label
    const getContentTypeInfo = (contentType?: string) => {
        switch (contentType) {
            case "CODE":
                return { icon: <CodeIcon sx={{ fontSize: 16 }} />, label: "Code", color: "#4CAF50" };
            case "DIAGRAM":
                return { icon: <DiagramIcon sx={{ fontSize: 16 }} />, label: "Diagram", color: "#FF9800" };
            case "WIREFRAME":
                return { icon: <WireframeIcon sx={{ fontSize: 16 }} />, label: "Wireframe", color: "#9C27B0" };
            case "MARKDOWN":
            default:
                return { icon: <MarkdownIcon sx={{ fontSize: 16 }} />, label: "Text", color: "#2196F3" };
        }
    };

    // ── Tab management ──────────────────────────────────────────────────

    /** Open a file as a tab (or switch to it if already open) */
    const openFileTab = useCallback(
        async (fileId: number, filePath: string) => {
            // If already open, just switch to it
            const existing = openTabs.find(t => t.fileId === fileId);
            if (existing) {
                setActiveTabId(fileId);
                const lang = getLanguageFromPath(filePath);
                setActiveView(lang === "markdown" ? "markdown" : "code");
                return;
            }

            // Fetch content
            try {
                if (!authToken) throw new Error("No authentication token available");

                const language = getLanguageFromPath(filePath);
                const filename = filePath.split("/").pop() || filePath;

                const response = await fetchWithAuth(
                    `${BACKEND_URI}/api/git/files/${fileId}/content`,
                    authToken,
                    { method: "GET" }
                );
                if (!response.ok) {
                    throw new Error(`Failed to fetch file content: ${response.statusText}`);
                }
                const data = await response.json();
                const content = data.file_content?.content || "";

                const newTab: EditorTab = { fileId, filePath, filename, content, language };

                setOpenTabs(prev => [...prev, newTab]);
                setActiveTabId(fileId);
                setActiveView(language === "markdown" ? "markdown" : "code");
            } catch (error) {
                console.error("Error loading file content:", error);
            }
        },
        [openTabs, authToken]
    );

    /** Persist a dirty editor buffer as the latest content revision. */
    const saveTab = useCallback(
        async (tab: EditorTab): Promise<boolean> => {
            if (!tab.dirty) return true;
            return persistFileContent(tab.fileId, tab.filename, tab.content);
        },
        [persistFileContent]
    );

    const triggerBrowserDownload = useCallback((blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    }, []);

    const downloadFile = useCallback(async (fileId: number, filePath: string) => {
        try {
            const openTab = openTabsRef.current.find(tab => tab.fileId === fileId);
            let content = openTab?.content;
            if (content === undefined) {
                if (!authToken) throw new Error("No authentication token available");
                const response = await fetchWithAuth(`/api/git/files/${fileId}/content`, authToken, { method: "GET" });
                if (!response.ok) throw new Error(`Failed to download file: ${response.statusText}`);
                const data = await response.json();
                content = data.file_content?.content ?? "";
            }
            triggerBrowserDownload(
                new Blob([content ?? ""], { type: "text/plain;charset=utf-8" }),
                filePath.split("/").pop() || `file-${fileId}`
            );
        } catch (error) {
            console.error("Error downloading file:", error);
            window.alert("The file could not be downloaded. Please try again.");
        }
    }, [authToken, triggerBrowserDownload]);

    const downloadRepository = useCallback(async () => {
        if (!repositoryId || !authToken || isDownloadingArchive) return;
        setIsDownloadingArchive(true);
        try {
            const dirtyTabs = openTabsRef.current.filter(tab => tab.dirty);
            const saved = await Promise.all(dirtyTabs.map(saveTab));
            if (saved.some(result => !result)) {
                throw new Error("One or more edited files could not be saved");
            }
            const response = await fetchWithAuth(`/api/git/repositories/${repositoryId}/archive`, authToken, { method: "GET" });
            if (!response.ok) throw new Error(`Failed to download repository: ${response.statusText}`);
            const disposition = response.headers.get("Content-Disposition") || "";
            const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || "repository.zip";
            triggerBrowserDownload(await response.blob(), filename);
        } catch (error) {
            console.error("Error downloading repository:", error);
            window.alert("The project could not be downloaded. Please try again.");
        } finally {
            setIsDownloadingArchive(false);
        }
    }, [authToken, isDownloadingArchive, repositoryId, saveTab, triggerBrowserDownload]);

    /** Close a tab, saving modified content before removing it. */
    const closeTab = useCallback(
        async (fileId: number, e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation(); // Don't trigger tab switch
            }
            const tab = openTabsRef.current.find(t => t.fileId === fileId);
            if (tab && !(await saveTab(tab))) {
                // Preserve the buffer when persistence fails so the user can retry.
                window.alert(`Could not save ${tab.filename}. The tab was left open.`);
                return;
            }
            setOpenTabs(prev => {
                const updated = prev.filter(t => t.fileId !== fileId);

                // If we closed the active tab, switch to another
                if (activeTabId === fileId) {
                    const closedIdx = prev.findIndex(t => t.fileId === fileId);
                    const nextTab = updated[Math.min(closedIdx, updated.length - 1)];
                    setActiveTabId(nextTab?.fileId ?? null);
                    if (nextTab) {
                        setActiveView(nextTab.language === "markdown" ? "markdown" : "code");
                    }
                }

                return updated;
            });
        },
        [activeTabId, saveTab]
    );

    /** Switch to a tab */
    const switchTab = useCallback((fileId: number) => {
        setActiveTabId(fileId);
        const tab = openTabs.find(t => t.fileId === fileId);
        if (tab) {
            setActiveView(tab.language === "markdown" ? "markdown" : "code");
        }
    }, [openTabs]);

    /** Update content for the active tab (when user edits in Monaco) */
    const handleEditorChange = useCallback(
        (value: string | undefined) => {
            if (readOnly) return;
            if (activeTabId == null) return;
            setOpenTabs(prev =>
                prev.map(t => (t.fileId === activeTabId ? { ...t, content: value || "", dirty: true } : t))
            );
        },
        [activeTabId, readOnly]
    );

    // Persist normal typing after a short idle period. Accepted agent edits are
    // saved immediately above; this covers direct edits made in Monaco.
    useEffect(() => {
        const dirtyTabs = openTabs.filter(tab => tab.dirty);
        if (dirtyTabs.length === 0) return;
        const timer = window.setTimeout(() => {
            dirtyTabs.forEach(tab => {
                void persistFileContent(tab.fileId, tab.filename, tab.content);
            });
        }, 750);
        return () => window.clearTimeout(timer);
    }, [openTabs, persistFileContent]);

    useEffect(() => {
        const { monacoAdapter } = getHostDocumentBridge();
        monacoAdapter.setReadOnly(readOnly);
        editorRef.current?.updateOptions?.({ readOnly, domReadOnly: readOnly });
        if (readOnly) {
            monacoAdapter.setStagingHandler(null);
            controllerRef.current?.dispose();
            controllerRef.current = null;
            setIsEditing(false);
        }
    }, [readOnly]);

    // Keep the host-document bridge in sync with open code tabs so the agent
    // sees what's open (active tab flagged) and lands edits in the right file.
    useEffect(() => {
        const { monacoAdapter } = getHostDocumentBridge();
        monacoAdapter.syncOpenDocs(
            openTabs.map((t) => ({
                fileId: t.fileId,
                filename: t.filename,
                filePath: t.filePath,
                language: t.language,
                content: t.content
            })),
            activeTabId
        );
        controllerRef.current?.setActiveFile(activeTabId);
    }, [openTabs, activeTabId]);

    useEffect(() => {
        return () => {
            const { monacoAdapter } = getHostDocumentBridge();
            monacoAdapter.syncOpenDocs([], null);
            monacoAdapter.setStagingHandler(null);
            monacoAdapter.setContentAccessor(null);
            controllerRef.current?.dispose();
            controllerRef.current = null;
            editorRef.current = null;
        };
    }, []);

    // Fetch repository ID when project changes
    useEffect(() => {
        const fetchRepositoryId = async () => {
            if (!selectedProject?.id || !authToken) {
                setRepositoryId(null);
                setIsLoadingRepo(false);
                return;
            }

            try {
                setIsLoadingRepo(true);
                const projectIdNum = typeof selectedProject.id === "string" ? parseInt(selectedProject.id, 10) : selectedProject.id;

                if (isNaN(projectIdNum)) {
                    console.error("Invalid project ID:", selectedProject.id);
                    setRepositoryId(null);
                    setIsLoadingRepo(false);
                    return;
                }

                const repoId = await getProjectRepositoryIdApi(projectIdNum, authToken);

                if (workProduct?.content_type === "CODE") {
                    // The warehouse sync is a bootstrap operation, not a load
                    // operation. Calling it whenever the editor mounts can put
                    // generated warehouse text back on top of a user-saved IDE
                    // revision. Check the repository first and sync only when
                    // this work product's artifact has not been created yet.
                    const normalizePath = (value: string) =>
                        value.replace(/\\/g, "/").replace(/^\.\//, "").replace(/^\//, "").toLowerCase();
                    const artifactPath = normalizePath(workProduct.artifact_path || "");
                    const artifactName = artifactPath.split("/").pop() || "";
                    let artifactExists = false;
                    if (artifactName) {
                        const files = await getGitFilesApi(repoId, authToken);
                        artifactExists = files.some(file => {
                            const path = normalizePath(String(file.path || file.filename || ""));
                            return path === artifactPath
                                || path.endsWith(`/${artifactPath}`)
                                || path.split("/").pop() === artifactName;
                        });
                    }

                    const rawFeatureId = Number(
                        workProduct.feature_id ?? selectedFeature?.id ?? (selectedFeature as any)?.featureid
                    );
                    const scopedFeatureId = Number.isSafeInteger(rawFeatureId) && rawFeatureId > 0 ? rawFeatureId : undefined;
                    const scopedStoryId = workProduct.user_story_id || undefined;
                    try {
                        if (artifactExists) {
                            setRepositoryId(repoId);
                            return;
                        }
                        await syncWorkProductCodeArtifact(
                            {
                                projectId: projectIdNum,
                                workproductId: Number(workProduct.id),
                                featureId: scopedFeatureId,
                                userStoryId: scopedStoryId,
                            },
                            authToken
                        );
                    } catch (syncError) {
                        // Best effort: IDE can still open repository files even when
                        // warehouse→git synchronization is unavailable.
                        console.warn("CODE artifact sync failed; continuing with repository lookup", syncError);
                    }
                }

                setRepositoryId(repoId);
            } catch (error) {
                console.error("Error fetching repository ID:", error);
                setRepositoryId(null);
            } finally {
                setIsLoadingRepo(false);
            }
        };

        fetchRepositoryId();
    }, [selectedProject?.id, authToken, selectedFeature?.id, workProduct?.content_type, workProduct?.feature_id, workProduct?.id, workProduct?.user_story_id]);

    // Initialize editor data when work product changes
    useEffect(() => {
        if (workProduct) {
            setEditedTitle(workProduct.name || "");
            setEditedDescription(workProduct.description || "");
            setSelectedSectionIndex(0);
            setSelectedSection(null);
        } else {
            setEditedTitle("");
            setEditedDescription("");
            setSelectedSectionIndex(0);
            setSelectedSection(null);
        }
    }, [workProduct]);

    if (!workProduct) {
        return (
            <MuiBox
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default"
                }}
            >
                <MuiTypography variant="h6" color="text.secondary">
                    No work product selected
                </MuiTypography>
            </MuiBox>
        );
    }

    const handleSave = () => {
        if (readOnly) return;
        console.log("Saving work product:", {
            id: workProduct.id,
            title: editedTitle,
            description: editedDescription
        });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedTitle(workProduct.name || "");
        setEditedDescription(workProduct.description || "");
        setIsEditing(false);
    };

    const handleSectionChange = (index: number, section: any) => {
        setSelectedSectionIndex(index);
        setSelectedSection(section);
    };

    // Resize handlers for left panel
    const handleMouseDown = React.useCallback(
        (e: React.MouseEvent) => {
            resizeStartX.current = e.clientX;
            resizeStartWidth.current = leftPanelWidth;
            setIsResizing(true);
        },
        [leftPanelWidth]
    );

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;
            const delta = e.clientX - resizeStartX.current;
            const newWidth = resizeStartWidth.current + delta;
            if (newWidth >= MIN_PANEL_WIDTH && newWidth <= MAX_PANEL_WIDTH) {
                setLeftPanelWidth(newWidth);
            }
        },
        [isResizing]
    );

    const handleMouseUp = React.useCallback(() => {
        setIsResizing(false);
    }, []);

    React.useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Get context data from props or use defaults
    const contextData = {
        feature: selectedFeature || DEFAULT_CONTEXT_DATA.feature,
        project: selectedProject || DEFAULT_CONTEXT_DATA.project
    };
    const contextProjectName = "name" in contextData.project
        ? contextData.project.name
        : contextData.project.project_name;

    return (
        <MuiBox
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                overflow: "hidden"
            }}
        >
            {/* Page.Header.Context - Standardized to 64px to match MUI AppBar */}
            <MuiPaper
                elevation={0}
                sx={{
                    bgcolor: "#F1F5FE",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 3,
                    height: 64,
                    minHeight: 64,
                    maxHeight: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    flexShrink: 0
                }}
            >
                {/* Left Section - Back Button & Work Product Title (H4) with Context Chips */}
                <MuiBox sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
                    <MuiTooltip title="Back to Feature Studios">
                        <MuiIconButton
                            onClick={onBack}
                            data-ui-bridge="btn.back-to-feature-studio"
                            data-ui-label="Back to Feature Studio"
                            data-ui-role="button"
                            data-ui-group="navigation"
                            data-ui-description="Return to Feature Studio from the editor"
                            sx={{
                                bgcolor: "background.paper",
                                color: "primary.main",
                                "&:hover": { bgcolor: "primary.main", color: "#FFFFFF" },
                                flexShrink: 0
                            }}
                        >
                            <ArrowBackIcon />
                        </MuiIconButton>
                    </MuiTooltip>

                    {/* Work Product Title (H4) */}
                    <MuiBox sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                            <MuiTextField
                                fullWidth
                                variant="outlined"
                                value={editedTitle}
                                onChange={e => setEditedTitle(e.target.value)}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        fontSize: "1.5rem",
                                        fontWeight: 600,
                                        bgcolor: "background.paper"
                                    }
                                }}
                                placeholder="Work Product Title"
                            />
                        ) : (
                            <>
                                <MuiTypography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                        fontSize: "1.5rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        flexShrink: 1,
                                        minWidth: 0
                                    }}
                                >
                                    {workProduct.name}
                                </MuiTypography>

                                {/* Context Information Chips */}
                                <MuiBox sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
                                    {(() => {
                                        const contentTypeInfo = getContentTypeInfo(workProduct.content_type ?? undefined);
                                        return (
                                            <MuiChip
                                                icon={contentTypeInfo.icon}
                                                label={contentTypeInfo.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: `${contentTypeInfo.color}20`,
                                                    color: contentTypeInfo.color,
                                                    borderColor: contentTypeInfo.color,
                                                    border: "1px solid",
                                                    fontWeight: 500,
                                                    "& .MuiChip-icon": {
                                                        color: contentTypeInfo.color
                                                    }
                                                }}
                                            />
                                        );
                                    })()}

                                    <MuiChip
                                        icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                                        label={contextData.feature.name}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: "primary.main",
                                            color: "primary.main",
                                            maxWidth: 200,
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }
                                        }}
                                    />

                                    <MuiChip
                                        icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                                        label={contextProjectName}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: "secondary.main",
                                            color: "text.primary",
                                            maxWidth: 200,
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }
                                        }}
                                    />
                                </MuiBox>
                            </>
                        )}
                    </MuiBox>
                </MuiBox>

                {/* Right Section - Actions */}
                <MuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <CollabAvatarStack />
                    <MuiTooltip title="Download ZIP">
                        <span>
                            <MuiIconButton
                                size="small"
                                onClick={downloadRepository}
                                disabled={!repositoryId || isDownloadingArchive}
                                aria-label="Download ZIP"
                                sx={{ color: "primary.main" }}
                            >
                                <DownloadIcon />
                            </MuiIconButton>
                        </span>
                    </MuiTooltip>
                    {readOnly ? <ViewOnlyIndicator /> : isEditing ? (
                        <>
                            <MuiButton variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} size="small">
                                Cancel
                            </MuiButton>
                            <MuiButton variant="contained" startIcon={<SaveIcon />} onClick={handleSave} size="small">
                                Save Changes
                            </MuiButton>
                        </>
                    ) : (
                        <>
                            <MuiTooltip title="Share Work Product">
                                <MuiIconButton size="small">
                                    <ShareIcon />
                                </MuiIconButton>
                            </MuiTooltip>
                            <MuiTooltip title="More Options">
                                <MuiIconButton size="small">
                                    <MoreVertIcon />
                                </MuiIconButton>
                            </MuiTooltip>
                        </>
                    )}
                </MuiBox>
            </MuiPaper>

            {/* App.Body.TwoColumn Layout */}
            <MuiBox sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {/* ── Activity Bar (VS Code-style) ────────────────────── */}
                <MuiBox
                    sx={{
                        width: 40,
                        minWidth: 40,
                        flexShrink: 0,
                        bgcolor: "#f1f5fe",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        pt: 1
                    }}
                >
                    {/* Separator top */}
                    <MuiBox
                        sx={{
                            width: 24,
                            height: "1px",
                            bgcolor: "divider",
                            mb: 0.5
                        }}
                    />

                    {/* Explorer */}
                    <MuiTooltip title="Explorer" placement="right">
                        <MuiIconButton
                            size="small"
                            onClick={() => setActiveSidePanel("explorer")}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "6px",
                                color: activeSidePanel === "explorer" ? "primary.main" : "text.secondary",
                                bgcolor: activeSidePanel === "explorer" ? "rgba(25,118,210,0.1)" : "transparent",
                                borderLeft: activeSidePanel === "explorer" ? "2px solid" : "2px solid transparent",
                                borderLeftColor: activeSidePanel === "explorer" ? "primary.main" : "transparent",
                                "&:hover": {
                                    bgcolor: activeSidePanel === "explorer" ? "rgba(25,118,210,0.15)" : "rgba(0,0,0,0.06)"
                                }
                            }}
                        >
                            <ExplorerIcon sx={{ fontSize: 20 }} />
                        </MuiIconButton>
                    </MuiTooltip>

                    {/* Search */}
                    <MuiTooltip title="Search" placement="right">
                        <MuiIconButton
                            size="small"
                            onClick={() => setActiveSidePanel("search")}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "6px",
                                mt: 0.5,
                                color: activeSidePanel === "search" ? "primary.main" : "text.secondary",
                                bgcolor: activeSidePanel === "search" ? "rgba(25,118,210,0.1)" : "transparent",
                                borderLeft: activeSidePanel === "search" ? "2px solid" : "2px solid transparent",
                                borderLeftColor: activeSidePanel === "search" ? "primary.main" : "transparent",
                                "&:hover": {
                                    bgcolor: activeSidePanel === "search" ? "rgba(25,118,210,0.15)" : "rgba(0,0,0,0.06)"
                                }
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 20 }} />
                        </MuiIconButton>
                    </MuiTooltip>

                    {/* Git / Source Control */}
                    <MuiTooltip title="Source Control" placement="right">
                        <MuiIconButton
                            size="small"
                            onClick={() => setActiveSidePanel("git")}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "6px",
                                mt: 0.5,
                                color: activeSidePanel === "git" ? "primary.main" : "text.secondary",
                                bgcolor: activeSidePanel === "git" ? "rgba(25,118,210,0.1)" : "transparent",
                                borderLeft: activeSidePanel === "git" ? "2px solid" : "2px solid transparent",
                                borderLeftColor: activeSidePanel === "git" ? "primary.main" : "transparent",
                                "&:hover": {
                                    bgcolor: activeSidePanel === "git" ? "rgba(25,118,210,0.15)" : "rgba(0,0,0,0.06)"
                                }
                            }}
                        >
                            <GitIcon sx={{ fontSize: 20 }} />
                        </MuiIconButton>
                    </MuiTooltip>

                    {/* Separator bottom */}
                    <MuiBox
                        sx={{
                            width: 24,
                            height: "1px",
                            bgcolor: "divider",
                            mt: 0.5
                        }}
                    />
                </MuiBox>

                {/* Column.Navigation - File tree (resizable width) */}
                <MuiBox
                    sx={{
                        width: leftPanelWidth,
                        flexShrink: 0,
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden"
                    }}
                >
                    <MuiPaper
                        elevation={1}
                        sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius: 0,
                            bgcolor: "#f1f5fe",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            borderRight: "1px solid",
                            borderColor: "divider"
                        }}
                    >
                        {/* Spacer to align with tab bar */}
                        <MuiBox
                            sx={{
                                height: 8,
                                minHeight: 8,
                                maxHeight: 8,
                                bgcolor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0
                            }}
                        />
                        {/* Side Panel Content — switches based on activity bar selection */}
                        <MuiBox sx={{ flex: 1, overflow: "auto" }}>
                            {activeSidePanel === "explorer" && (
                                <>
                                    {isLoadingRepo ? (
                                        <MuiBox sx={{ p: 3, textAlign: "center" }}>
                                            <MuiTypography variant="body2" color="text.secondary">
                                                Loading repository...
                                            </MuiTypography>
                                        </MuiBox>
                                    ) : !repositoryId ? (
                                        <MuiBox sx={{ p: 3, textAlign: "center" }}>
                                            <MuiTypography variant="body2" color="text.secondary">
                                                No repository found for this product
                                            </MuiTypography>
                                        </MuiBox>
                                    ) : workProduct ? (
                                        <WorkProductEditorFileList
                                            repositoryId={repositoryId}
                                            onFileSelect={openFileTab}
                                            onFileDownload={downloadFile}
                                            presencePeers={presencePeers}
                                            initialFilePath={workProduct.artifact_path}
                                            autoSelectKey={workProduct.id}
                                        />
                                    ) : null}
                                </>
                            )}

                            {activeSidePanel === "search" && (
                                <MuiBox sx={{ p: 2 }}>
                                    <MuiTypography
                                        variant="caption"
                                        sx={{
                                            textTransform: "uppercase",
                                            fontWeight: 600,
                                            letterSpacing: "0.05em",
                                            color: "text.secondary",
                                            mb: 1,
                                            display: "block"
                                        }}
                                    >
                                        Search
                                    </MuiTypography>
                                    <MuiTypography variant="body2" color="text.disabled">
                                        Search functionality coming soon.
                                    </MuiTypography>
                                </MuiBox>
                            )}

                            {activeSidePanel === "git" && (
                                <MuiBox sx={{ p: 2 }}>
                                    <MuiTypography
                                        variant="caption"
                                        sx={{
                                            textTransform: "uppercase",
                                            fontWeight: 600,
                                            letterSpacing: "0.05em",
                                            color: "text.secondary",
                                            mb: 1,
                                            display: "block"
                                        }}
                                    >
                                        Source Control
                                    </MuiTypography>
                                    <MuiTypography variant="body2" color="text.disabled">
                                        Git workflow coming soon.
                                    </MuiTypography>
                                </MuiBox>
                            )}
                        </MuiBox>
                    </MuiPaper>

                    {/* Resize Handle */}
                    <MuiBox
                        onMouseDown={handleMouseDown}
                        sx={{
                            width: "4px",
                            height: "100%",
                            position: "absolute",
                            right: 0,
                            top: 0,
                            cursor: "col-resize",
                            bgcolor: "transparent",
                            zIndex: 10,
                            "&:hover": {
                                bgcolor: "primary.main",
                                opacity: 0.5
                            },
                            "&:active": {
                                bgcolor: "primary.main",
                                opacity: 0.8
                            },
                            transition: "background-color 0.2s"
                        }}
                    />
                </MuiBox>

                {/* Editor Content Area */}
                <MuiBox sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* ── Tab Bar ─────────────────────────────────────────── */}
                    <MuiBox
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            bgcolor: "#e8e8e8",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            height: 35,
                            minHeight: 35,
                            flexShrink: 0,
                            overflow: "hidden"
                        }}
                    >
                        {/* Scrollable tab list */}
                        <MuiBox
                            ref={tabContainerRef}
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "stretch",
                                overflow: "hidden",
                                overflowX: "auto",
                                "&::-webkit-scrollbar": { height: 0 }
                            }}
                        >
                            {openTabs.map(tab => {
                                const isActive = tab.fileId === activeTabId;
                                const isDragging = tab.fileId === dragTabId;
                                const isDropTarget = tab.fileId === dropTargetId;
                                return (
                                    <MuiBox
                                        key={tab.fileId}
                                        data-tab-id={tab.fileId}
                                        draggable
                                        onDragStart={e => handleTabDragStart(e, tab.fileId)}
                                        onDragOver={e => handleTabDragOver(e, tab.fileId)}
                                        onDrop={e => handleTabDrop(e, tab.fileId)}
                                        onDragEnd={handleTabDragEnd}
                                        onClick={() => switchTab(tab.fileId)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            px: 1.5,
                                            cursor: "grab",
                                            userSelect: "none",
                                            whiteSpace: "nowrap",
                                            borderRight: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: isActive ? "#ffffff" : "transparent",
                                            borderBottom: isActive ? "1px solid #ffffff" : "1px solid transparent",
                                            mb: isActive ? "-1px" : 0,
                                            // Drag visual feedback
                                            opacity: isDragging ? 0.4 : 1,
                                            borderLeft: isDropTarget ? "2px solid" : "none",
                                            borderLeftColor: isDropTarget ? "primary.main" : "transparent",
                                            transition: "opacity 0.15s, border-left 0.1s",
                                            "&:hover": {
                                                bgcolor: isActive ? "#ffffff" : "#d4d4d4"
                                            },
                                            "&:hover .tab-close": {
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        {/* File icon */}
                                        {getFileIcon(tab.filename, FILE_ICON_SIZE)}

                                        {/* File name */}
                                        <MuiTypography
                                            component="span"
                                            sx={{
                                                fontSize: 13,
                                                lineHeight: "35px",
                                                fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
                                                color: isActive ? "text.primary" : "text.secondary"
                                            }}
                                        >
                                            {tab.filename}
                                        </MuiTypography>

                                        {presencePeers
                                            .filter(peer => peer.activity?.target.kind === "file" && peer.activity.target.id === String(tab.fileId))
                                            .slice(0, 3)
                                            .map(peer => (
                                                <MuiBox
                                                    key={`${peer.user!.userid}:${tab.fileId}`}
                                                    aria-label={`${peer.user!.name} is ${peer.activity!.activity}`}
                                                    title={`${peer.user!.name} is ${peer.activity!.activity}`}
                                                    sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: peer.user!.color, flexShrink: 0 }}
                                                />
                                            ))}

                                        {/* Close button */}
                                        <MuiBox
                                            className="tab-close"
                                            onClick={e => closeTab(tab.fileId, e)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 18,
                                                height: 18,
                                                borderRadius: "3px",
                                                ml: 0.5,
                                                cursor: "pointer",
                                                opacity: isActive ? 1 : 0,
                                                transition: "opacity 0.1s, background-color 0.1s",
                                                "&:hover": {
                                                    bgcolor: "rgba(0,0,0,0.1)"
                                                }
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                        </MuiBox>
                                    </MuiBox>
                                );
                            })}
                        </MuiBox>

                        {/* Tab overflow dropdown */}
                        {tabsOverflow && openTabs.length > 0 && (
                            <>
                                <MuiTooltip title="Open Tabs">
                                    <MuiIconButton
                                        size="small"
                                        onClick={e => setTabMenuAnchor(e.currentTarget)}
                                        sx={{
                                            width: 28,
                                            height: 35,
                                            borderRadius: 0,
                                            borderLeft: "1px solid",
                                            borderColor: "divider",
                                            color: "text.secondary",
                                            flexShrink: 0,
                                            "&:hover": {
                                                bgcolor: "#d4d4d4"
                                            }
                                        }}
                                    >
                                        <ChevronDownIcon sx={{ fontSize: 18 }} />
                                    </MuiIconButton>
                                </MuiTooltip>
                                <MuiMenu
                                    anchorEl={tabMenuAnchor}
                                    open={Boolean(tabMenuAnchor)}
                                    onClose={() => setTabMenuAnchor(null)}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                maxHeight: 300,
                                                minWidth: 200,
                                                mt: 0.5
                                            }
                                        }
                                    }}
                                >
                                    {openTabs.map(tab => (
                                        <MuiMenuItem
                                            key={tab.fileId}
                                            selected={tab.fileId === activeTabId}
                                            onClick={() => {
                                                switchTab(tab.fileId);
                                                setTabMenuAnchor(null);
                                                // Scroll the tab into view
                                                const el = tabContainerRef.current;
                                                if (el) {
                                                    const tabEl = el.querySelector(`[data-tab-id="${tab.fileId}"]`);
                                                    tabEl?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
                                                }
                                            }}
                                            sx={{
                                                py: 0.75,
                                                px: 1.5,
                                                gap: 1,
                                                fontSize: 13,
                                                fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif"
                                            }}
                                        >
                                            <MuiListItemIcon sx={{ minWidth: "24px !important" }}>
                                                {getFileIcon(tab.filename, FILE_ICON_SIZE)}
                                            </MuiListItemIcon>
                                            <MuiListItemText
                                                primary={tab.filename}
                                                secondary={tab.filePath}
                                                primaryTypographyProps={{ fontSize: 13, fontWeight: tab.fileId === activeTabId ? 600 : 400 }}
                                                secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                                            />
                                        </MuiMenuItem>
                                    ))}
                                </MuiMenu>
                            </>
                        )}

                        {/* Right side of tab bar: view toggle icons (always rendered, visibility-controlled to prevent layout shift) */}
                        <MuiBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                                px: 1,
                                flexShrink: 0,
                                borderLeft: "1px solid",
                                borderColor: activeTab?.language === "markdown" ? "divider" : "transparent",
                                visibility: activeTab?.language === "markdown" ? "visible" : "hidden",
                                pointerEvents: activeTab?.language === "markdown" ? "auto" : "none"
                            }}
                        >
                            <MuiTooltip title="Source Code">
                                <MuiIconButton
                                    size="small"
                                    onClick={() => setActiveView("code")}
                                    data-ui-bridge="btn.source-code"
                                    data-ui-label="Source Code"
                                    data-ui-role="button"
                                    data-ui-group="editor-view"
                                    data-ui-description="Switch the active Markdown file to editable Source Code view"
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "4px",
                                        color: activeView === "code" ? "primary.main" : "text.secondary",
                                        bgcolor: activeView === "code" ? "rgba(25,118,210,0.08)" : "transparent",
                                        "&:hover": {
                                            bgcolor: activeView === "code" ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.06)"
                                        }
                                    }}
                                >
                                    <CodeIcon sx={{ fontSize: 18 }} />
                                </MuiIconButton>
                            </MuiTooltip>
                            <MuiTooltip title="Markdown Preview">
                                <MuiIconButton
                                    size="small"
                                    onClick={() => setActiveView("markdown")}
                                    data-ui-bridge="btn.markdown-preview"
                                    data-ui-label="Markdown Preview"
                                    data-ui-role="button"
                                    data-ui-group="editor-view"
                                    data-ui-description="Switch the active Markdown file to rendered Markdown Preview"
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "4px",
                                        color: activeView === "markdown" ? "primary.main" : "text.secondary",
                                        bgcolor: activeView === "markdown" ? "rgba(25,118,210,0.08)" : "transparent",
                                        "&:hover": {
                                            bgcolor: activeView === "markdown" ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.06)"
                                        }
                                    }}
                                >
                                    <MarkdownIcon sx={{ fontSize: 18 }} />
                                </MuiIconButton>
                            </MuiTooltip>
                        </MuiBox>
                    </MuiBox>

                    {/* ── Editor / Viewer Area ────────────────────────────── */}
                    <MuiBox
                        sx={{ flex: 1, overflow: "hidden", position: "relative" }}
                        onFocusCapture={() => setEditorFocused(true)}
                        onBlurCapture={event => {
                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setEditorFocused(false);
                        }}
                        onInputCapture={() => { if (!readOnly) setEditorFocused(true); }}
                    >
                        {!activeTab ? (
                            // Empty state — no file open
                            <MuiBox
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "#fafafa"
                                }}
                            >
                                <MuiTypography variant="body1" color="text.disabled">
                                    Select a file from the explorer to open it
                                </MuiTypography>
                            </MuiBox>
                        ) : activeView === "code" || activeTab.language !== "markdown" ? (
                            <Editor
                                key={readOnly ? "monaco-read-only" : "monaco-write"}
                                height="100%"
                                language={activeTab.language}
                                path={activeTab.filePath}
                                keepCurrentModel={false}
                                value={activeTab.content}
                                onChange={handleEditorChange}
                                beforeMount={handleEditorWillMount}
                                onMount={handleEditorDidMount}
                                options={{
                                    readOnly,
                                    domReadOnly: readOnly,
                                    quickSuggestions: false,
                                    suggestOnTriggerCharacters: false,
                                    parameterHints: { enabled: false },
                                    formatOnType: false,
                                    formatOnPaste: false,
                                    autoClosingBrackets: "never",
                                    autoClosingQuotes: "never",
                                    codeLens: false,
                                    glyphMargin: true,
                                    lightbulb: { enabled: true },
                                    renderValidationDecorations: "off"
                                }}
                            />
                        ) : (
                            <MuiBox
                                sx={{
                                    height: "100%",
                                    overflow: "auto",
                                    bgcolor: "background.paper",
                                    p: 4,
                                    "& pre": {
                                        bgcolor: "#f5f5f5",
                                        p: 2,
                                        borderRadius: 1,
                                        overflow: "auto"
                                    },
                                    "& code": {
                                        bgcolor: "#f5f5f5",
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        fontSize: "0.875em",
                                        fontFamily: "monospace"
                                    },
                                    "& h1": {
                                        fontSize: "2em",
                                        fontWeight: 600,
                                        mt: 3,
                                        mb: 2,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        pb: 1
                                    },
                                    "& h2": {
                                        fontSize: "1.5em",
                                        fontWeight: 600,
                                        mt: 3,
                                        mb: 2,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        pb: 1
                                    },
                                    "& h3": {
                                        fontSize: "1.25em",
                                        fontWeight: 600,
                                        mt: 2.5,
                                        mb: 1.5
                                    },
                                    "& h4": {
                                        fontSize: "1.1em",
                                        fontWeight: 600,
                                        mt: 2,
                                        mb: 1.5
                                    },
                                    "& h5": {
                                        fontSize: "1em",
                                        fontWeight: 600,
                                        mt: 2,
                                        mb: 1
                                    },
                                    "& h6": {
                                        fontSize: "0.9em",
                                        fontWeight: 600,
                                        mt: 2,
                                        mb: 1,
                                        color: "text.secondary"
                                    },
                                    "& p": {
                                        mb: 2,
                                        lineHeight: 1.7
                                    },
                                    "& ul": {
                                        mb: 2,
                                        pl: 4,
                                        listStyleType: "disc",
                                        "& ul": {
                                            listStyleType: "circle",
                                            "& ul": {
                                                listStyleType: "square"
                                            }
                                        }
                                    },
                                    "& ol": {
                                        mb: 2,
                                        pl: 4,
                                        listStyleType: "decimal"
                                    },
                                    "& li": {
                                        mb: 0.5,
                                        lineHeight: 1.7,
                                        "& > p": {
                                            mb: 0.5
                                        }
                                    },
                                    "& blockquote": {
                                        borderLeft: "4px solid",
                                        borderColor: "divider",
                                        pl: 2,
                                        py: 1,
                                        my: 2,
                                        fontStyle: "italic",
                                        color: "text.secondary"
                                    },
                                    "& table": {
                                        borderCollapse: "collapse",
                                        width: "100%",
                                        mb: 2
                                    },
                                    "& th, & td": {
                                        border: "1px solid",
                                        borderColor: "divider",
                                        p: 1,
                                        textAlign: "left"
                                    },
                                    "& th": {
                                        bgcolor: "#f5f5f5",
                                        fontWeight: 600
                                    },
                                    "& .ide-mermaid-diagram": {
                                        display: "flex",
                                        justifyContent: "center",
                                        overflowX: "auto",
                                        my: 2,
                                        "& svg": {
                                            maxWidth: "100%",
                                            height: "auto",
                                            overflow: "visible"
                                        },
                                        // Mermaid measures node labels before the surrounding
                                        // Markdown typography rules are applied. Reset those
                                        // rules so wrapped final lines remain inside the node.
                                        "& p": {
                                            margin: 0,
                                            lineHeight: "normal"
                                        },
                                        "& .nodeLabel": {
                                            lineHeight: "normal"
                                        }
                                    }
                                }}
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{ pre: MarkdownPre }}
                                >
                                    {activeTab.content}
                                </ReactMarkdown>
                            </MuiBox>
                        )}
                    </MuiBox>
                </MuiBox>
            </MuiBox>
        </MuiBox>
    );
};

export default WorkProductEditor_Code;
